import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import CodeBlock from '@/components/layout/CodeBlock';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import {
    mdiCheckCircle,
    mdiLoading,
    mdiAlertCircleOutline,
    mdiClockAlertOutline,
    mdiArrowRight,
    mdiContentCopy,
    mdiRouterWireless,
    mdiRefresh,
    mdiPencilOutline,
} from '@mdi/js';
import { extractApiError } from '@/lib/apiError';
import { toastError } from '@/lib/toast';

type Vendor = 'mikrotik' | 'opnsense';
type Platform = 'linux' | 'windows';

/** Resposta de POST /enrollment_token/install_command — o comando de uma
 *  linha que entrega o token; o script em si é estático e público. */
type InstallCommandResult = {
    ok: boolean;
    agent_id: string;
    token: string;
    vendor: Vendor;
    platform: Platform;
    expira_em: string;
    command: string;
};

type StatusResult = {
    ok: boolean;
    connected: boolean;
    agent: { version: string; agent_type: string; connected_at: string; install_type?: string } | null;
};

/** Espelha MndpCandidate do backend (agent-ws-service.ts). */
type MndpCandidate = {
    mac: string;
    identity: string;
    version?: string;
    board?: string;
    ipv4?: string;
};

type DiscoverResult = {
    ok: boolean;
    candidates: MndpCandidate[];
};

/** Identity de fábrica — RouterOS nunca configurado ainda não vale como "nome". */
const FACTORY_IDENTITY = 'MikroTik';

/** Espelha o número usado na revisão de onboarding (D12) — diagnóstico
 *  aparece depois disso, mas o polling continua: o agente ainda pode conectar. */
const WAIT_TIMEOUT_MS = 90_000;
const POLL_INTERVAL_MS = 2_000;

function detectPlatform(): Platform {
    return navigator.userAgent.toLowerCase().includes('windows') ? 'windows' : 'linux';
}

function copyToClipboard(text: string, onDone: () => void) {
    navigator.clipboard.writeText(text).then(onDone);
}

const VendorToggle = ({ value, onChange }: { value: Vendor; onChange: (v: Vendor) => void }) => (
    <HBox style={{ gap: '8px' }}>
        <Botao variant={value === 'mikrotik' ? 'primary' : 'outline-secondary'} onClick={() => onChange('mikrotik')}>
            MikroTik
        </Botao>
        <Botao variant={value === 'opnsense' ? 'primary' : 'outline-secondary'} onClick={() => onChange('opnsense')}>
            OPNsense
        </Botao>
    </HBox>
);

const PlatformToggle = ({ value, onChange }: { value: Platform; onChange: (p: Platform) => void }) => (
    <HBox style={{ gap: '8px' }}>
        <Botao variant={value === 'linux' ? 'primary' : 'outline-secondary'} onClick={() => onChange('linux')}>
            Linux / macOS
        </Botao>
        <Botao variant={value === 'windows' ? 'primary' : 'outline-secondary'} onClick={() => onChange('windows')}>
            Windows
        </Botao>
    </HBox>
);

/** Comandos de diagnóstico prontos pra copiar — nunca "falha ao conectar" sem explicação (D12). */
const DiagnosticoTimeout = ({ host, platform }: { host: string; platform: Platform }) => {
    const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

    const comandos = platform === 'windows'
        ? [
            { titulo: 'O host resolve?', comando: `Resolve-DnsName ${host}` },
            { titulo: 'A porta 443 está alcançável?', comando: `Test-NetConnection -ComputerName ${host} -Port 443` },
            { titulo: 'Tem proxy corporativo na frente?', comando: `netsh winhttp show proxy` },
        ]
        : [
            { titulo: 'O host resolve?', comando: `getent hosts ${host} || nslookup ${host}` },
            { titulo: 'A porta 443 está alcançável?', comando: `curl -v https://${host}/api/agent/release` },
            { titulo: 'Tem proxy corporativo na frente?', comando: `echo "$http_proxy $https_proxy"` },
        ];

    return (
        <AlertBox variant="warning">
            <VBox style={{ gap: '10px' }}>
                <HBox style={{ gap: '8px', alignItems: 'center' }}>
                    <Icon path={mdiClockAlertOutline} size="20px" />
                    <strong>O agente ainda não apareceu.</strong>
                </HBox>
                <p style={{ margin: 0 }}>
                    Continuamos esperando — se ele conectar, esta tela atualiza sozinha. Enquanto isso, rode isto na máquina onde o agente está instalado:
                </p>
                <VBox style={{ gap: '6px' }}>
                    {comandos.map((c, i) => (
                        <HBox key={i} style={{ justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <VBox style={{ gap: '2px', flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: '12px', fontWeight: 600 }}>{c.titulo}</span>
                                <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>{c.comando}</code>
                            </VBox>
                            <Botao
                                variant={copiedIdx === i ? 'none-success' : 'none-secondary'}
                                icon={mdiContentCopy}
                                onClick={() => copyToClipboard(c.comando, () => { setCopiedIdx(i); setTimeout(() => setCopiedIdx(null), 2000); })}
                            />
                        </HBox>
                    ))}
                </VBox>
            </VBox>
        </AlertBox>
    );
};

/** O que a modal já sabe na hora de abrir o formulário de appliance — cadastro
 *  manual continua sendo o passo final (adoção completa ainda não existe),
 *  mas agora chega pré-preenchido quando um candidato MNDP foi escolhido. */
type AdoptionPrefill = {
    agentId: string;
    vendor: Vendor;
    endereco?: string;
    nome?: string;
};

type Props = {
    show: boolean;
    onClose: () => void;
    onAgentConnected: (prefill: AdoptionPrefill) => void;
    /**
     * Quando presente, gera o instalador pra um agente que JÁ existe
     * (alvo_tipo 'agente_existente') em vez de criar um novo: reinstalação da
     * máquina, troca de host ou uma segunda máquina do mesmo adapter. O
     * agent_id não é escolhido pelo operador em nenhum dos dois casos — aqui
     * ele vem da linha da tela de agentes, lá o backend gera.
     */
    agentId?: string;
};

/**
 * Persistência do fluxo pendente — quem garante "fechar e voltar não perde o
 * progresso" (D12) não é isto, é o backend (agent_id/status independem da
 * aba). Isto só evita que FECHAR O MODAL jogue fora o resultado que o
 * backend já entregou (script, agent_id) e obrigue gerar tudo de novo.
 * Por ficar em localStorage, o alcance real é "mesmo navegador", não
 * qualquer aparelho — vale deixar isso explícito na tela, não implícito.
 */
const PENDING_KEY = 'webip:install-agent:pending';

/** Um instalador pendente por agente-alvo: reinstalar 'agt-x' não pode
 *  atropelar (nem herdar) o fluxo de um agente novo aberto antes. */
function pendingKey(agentId?: string) {
    return agentId ? `${PENDING_KEY}:${agentId}` : PENDING_KEY;
}

type PendingState = { result: InstallCommandResult; vendor: Vendor; platform: Platform; startedAt: number };

function loadPending(key: string): PendingState | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PendingState;
        // Formato antigo (script inteiro em vez de comando) — descarta em vez
        // de renderizar um CodeBlock vazio pra quem tinha um fluxo pendente.
        if (!parsed?.result?.command) {
            localStorage.removeItem(key);
            return null;
        }
        if (new Date(parsed.result.expira_em).getTime() < Date.now()) {
            localStorage.removeItem(key);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function savePending(key: string, state: PendingState | null) {
    try {
        if (state) localStorage.setItem(key, JSON.stringify(state));
        else localStorage.removeItem(key);
    } catch {
        // localStorage indisponível (modo privado, quota) — degrada pra
        // "fechar perde o progresso", não quebra o fluxo.
    }
}

const InstallAgentModal = ({ show, onClose, onAgentConnected, agentId }: Props) => {
    const storageKey = pendingKey(agentId);
    const pending = loadPending(storageKey);
    const [vendor, setVendor] = useState<Vendor>(pending?.vendor ?? 'mikrotik');
    const [platform, setPlatform] = useState<Platform>(pending?.platform ?? detectPlatform());
    const [result, setResult] = useState<InstallCommandResult | null>(pending?.result ?? null);
    const [startedAt, setStartedAt] = useState<number | null>(pending?.startedAt ?? null);
    const [elapsedMs, setElapsedMs] = useState(pending ? Date.now() - pending.startedAt : 0);

    const mutGerar = useMutation({
        mutationFn: async () => {
            const res = await axios.post<InstallCommandResult>('/api/enrollment_token/install_command', {
                alvo_tipo: agentId ? 'agente_existente' : 'novo_agente',
                ...(agentId ? { agent_id: agentId } : {}),
                vendor,
                platform,
            }, { withCredentials: true });
            return res.data;
        },
        onSuccess: (data) => {
            const now = Date.now();
            setResult(data);
            setStartedAt(now);
            setElapsedMs(0);
            savePending(storageKey, { result: data, vendor, platform, startedAt: now });
        },
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao gerar o instalador');
            toastError(message);
        },
    });

    const queryStatus = useQuery<StatusResult>({
        queryKey: ['enrollment-status', result?.agent_id],
        queryFn: async () => {
            const res = await axios.get<StatusResult>(`/api/enrollment_token/${result!.agent_id}/status`, { withCredentials: true });
            return res.data;
        },
        enabled: !!result?.agent_id,
        refetchInterval: (data) => (data?.connected ? false : POLL_INTERVAL_MS),
    });

    const connected = queryStatus.data?.connected ?? false;

    // Descoberta só faz sentido pra MikroTik (MNDP é protocolo proprietário
    // deles) — OPNsense sempre cai direto no cadastro manual.
    const queryDiscover = useQuery<DiscoverResult>({
        queryKey: ['mndp-discover', result?.agent_id],
        queryFn: async () => {
            const res = await axios.get<DiscoverResult>(`/api/enrollment_token/${result!.agent_id}/discover`, { withCredentials: true });
            return res.data;
        },
        enabled: connected && vendor === 'mikrotik' && !!result?.agent_id,
        refetchInterval: 5_000,
    });

    // Cronômetro só pra decidir quando mostrar o diagnóstico — o polling em
    // si já roda sozinho via refetchInterval, independente disto.
    useEffect(() => {
        if (!startedAt || connected) return;
        const id = setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
        return () => clearInterval(id);
    }, [startedAt, connected]);

    const timedOut = elapsedMs >= WAIT_TIMEOUT_MS && !connected;

    // Só fecha a modal — o resultado pendente fica salvo (ver savePending) e
    // reaparece na próxima vez que abrir, sem gerar tudo de novo.
    const handleClose = () => onClose();

    // Abandona de vez: usado quando o operador quer trocar de equipamento/
    // plataforma em vez de continuar esperando o que já foi gerado.
    const handleCancel = () => {
        savePending(storageKey, null);
        setResult(null);
        setStartedAt(null);
        setElapsedMs(0);
    };

    return (
        <Modal show={show} title={agentId ? `Reinstalar agente ${agentId}` : 'Instalar agente'} onClose={handleClose} hideControls size="md">
            <VBox style={{ gap: '20px', minWidth: '460px' }}>
                {!result && (
                    <>
                        <AlertBox variant="info">
                            O agente roda perto (ou no) roteador e conecta na nossa plataforma sozinho — nada de porta aberta na sua rede. Escolha o equipamento e o sistema onde o agente vai rodar.
                        </AlertBox>

                        <VBox style={{ gap: '6px' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>Equipamento</span>
                            <VendorToggle value={vendor} onChange={setVendor} />
                        </VBox>

                        <VBox style={{ gap: '6px' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>Onde o agente vai rodar</span>
                            <PlatformToggle value={platform} onChange={setPlatform} />
                        </VBox>

                        <HBox style={{ justifyContent: 'flex-end' }}>
                            <Botao variant="primary" onClick={() => mutGerar.mutate()} disabled={mutGerar.isLoading}>
                                {mutGerar.isLoading ? <Icon path={mdiLoading} spin size="18px" /> : 'Gerar comando'}
                            </Botao>
                        </HBox>
                    </>
                )}

                {result && !connected && (
                    <>
                        <VBox style={{ gap: '6px' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>
                                Cole este comando no terminal {platform === 'windows' ? '(PowerShell, como administrador)' : '(com sudo)'} da máquina:
                            </span>
                            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                O instalador baixa o binário, confere o sha256, escreve a configuração deste agente e registra o serviço. O script é público e igual pra todo mundo — dá pra abrir a URL no navegador e ler antes de rodar; o que identifica esta instalação é o token do comando.
                            </span>
                            <CodeBlock value={result.command} />
                        </VBox>

                        <AlertBox variant="info">
                            <HBox style={{ gap: '10px', alignItems: 'center' }}>
                                <Icon path={mdiLoading} spin size="20px" />
                                <VBox style={{ gap: '2px' }}>
                                    <strong>Aguardando o agente conectar…</strong>
                                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                        Pode fechar esta janela e voltar depois, neste mesmo navegador — o progresso fica salvo.
                                    </span>
                                </VBox>
                            </HBox>
                        </AlertBox>

                        {timedOut && <DiagnosticoTimeout host={window.location.host} platform={platform} />}

                        <HBox style={{ justifyContent: 'flex-end' }}>
                            <Botao variant="none-secondary" onClick={handleCancel}>
                                Cancelar e escolher outro equipamento
                            </Botao>
                        </HBox>
                    </>
                )}

                {result && connected && queryStatus.data?.agent && (
                    <VBox style={{ gap: '16px' }}>
                        <AlertBox variant="success">
                            <HBox style={{ gap: '8px', alignItems: 'center' }}>
                                <Icon path={mdiCheckCircle} size="22px" />
                                <strong>Agente conectado! (v{queryStatus.data.agent.version})</strong>
                            </HBox>
                        </AlertBox>

                        {vendor === 'mikrotik' ? (
                            <VBox style={{ gap: '10px' }}>
                                <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: '14px' }}>Equipamentos vistos na rede</span>
                                    <Botao
                                        variant="none-secondary"
                                        icon={queryDiscover.isFetching ? mdiLoading : mdiRefresh}
                                        onClick={() => queryDiscover.refetch()}
                                        disabled={queryDiscover.isFetching}
                                    >
                                        {queryDiscover.isFetching ? '' : 'Atualizar'}
                                    </Botao>
                                </HBox>

                                {!queryDiscover.data && queryDiscover.isLoading && (
                                    <HBox style={{ gap: '8px', alignItems: 'center', color: 'var(--tc-color-gray-600)', fontSize: '13px' }}>
                                        <Icon path={mdiLoading} spin size="16px" />
                                        Procurando roteadores MikroTik na rede local…
                                    </HBox>
                                )}

                                {queryDiscover.data?.candidates.length === 0 && (
                                    <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                                        Nenhum equipamento apareceu ainda. O roteador anuncia a cada alguns segundos — clique em "Atualizar" ou cadastre manualmente abaixo.
                                    </span>
                                )}

                                {(queryDiscover.data?.candidates.length ?? 0) > 0 && (
                                    <VBox style={{ gap: '6px' }}>
                                        {queryDiscover.data!.candidates.map((cand) => (
                                            <HBox
                                                key={cand.mac}
                                                style={{
                                                    justifyContent: 'space-between', alignItems: 'center', gap: '10px',
                                                    padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--tc-color-gray-200)',
                                                }}
                                            >
                                                <HBox style={{ gap: '10px', alignItems: 'center', minWidth: 0 }}>
                                                    <Icon path={mdiRouterWireless} size="20px" />
                                                    <VBox style={{ gap: '1px', minWidth: 0 }}>
                                                        <strong style={{ fontSize: '13px' }}>
                                                            {cand.identity === FACTORY_IDENTITY ? 'Roteador não configurado' : cand.identity}
                                                            {cand.board ? ` — ${cand.board}` : ''}
                                                        </strong>
                                                        <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                                            {cand.ipv4 ?? 'IP não capturado'} · {cand.mac}{cand.version ? ` · RouterOS ${cand.version}` : ''}
                                                        </span>
                                                    </VBox>
                                                </HBox>
                                                <Botao
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        savePending(storageKey, null);
                                                        onAgentConnected({
                                                            agentId: result.agent_id,
                                                            vendor,
                                                            endereco: cand.ipv4,
                                                            nome: cand.board || (cand.identity !== FACTORY_IDENTITY ? cand.identity : undefined),
                                                        });
                                                        handleClose();
                                                    }}
                                                >
                                                    Adotar
                                                </Botao>
                                            </HBox>
                                        ))}
                                    </VBox>
                                )}
                            </VBox>
                        ) : (
                            <p style={{ margin: 0, fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                                Descoberta automática só existe pra MikroTik por enquanto — cadastre o OPNsense manualmente.
                            </p>
                        )}

                        <HBox style={{ justifyContent: 'flex-end' }}>
                            <Botao
                                variant="none-secondary"
                                icon={mdiPencilOutline}
                                onClick={() => {
                                    savePending(storageKey, null);
                                    onAgentConnected({ agentId: result.agent_id, vendor });
                                    handleClose();
                                }}
                            >
                                Cadastrar equipamento
                            </Botao>
                        </HBox>
                    </VBox>
                )}

                {queryStatus.isError && (
                    <AlertBox variant="danger">
                        <HBox style={{ gap: '8px', alignItems: 'center' }}>
                            <Icon path={mdiAlertCircleOutline} size="18px" />
                            Erro ao consultar o status do agente. Tentando de novo…
                        </HBox>
                    </AlertBox>
                )}
            </VBox>
        </Modal>
    );
};

export default InstallAgentModal;
